import { Box, Checkbox, FormControlLabel, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReplayIcon from "@mui/icons-material/Replay";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import DotLegalSelectWithButton from "@common/components/dotLegalSelectWithButton/DotLegalSelectWithButton";
import { useTranslation } from "@localization/useTranslation";
import { SelectableLegalUnitsItem } from "../../processingActivity/ProcessingActivity.types";
import { ReviewParty } from "./ContractAiUpdate.types.ts";
import { useContractAiUpdateStyles } from "./ContractAiUpdate.styles.ts";
import ContractAiUpdateCitation from "./ContractAiUpdateCitation.tsx";
import OverflowTooltip from "@common/components/OverflowTooltip";

export interface CounterPartiesReviewFieldProps {
    parties: Array<ReviewParty>;
    selectableLegalEntities: Array<SelectableLegalUnitsItem> | undefined;
    isLoading: boolean;
    readOnly: boolean;
    onRemove: (key: string) => void;
    onToggleIncluded: (key: string) => void;
    onAdd: (legalEntityId: string) => void;
    onShowInDocument: (quote: string) => void;
    /** The read-only column only: whether this party of the contract's own is in the proposal, and can be. */
    getToggleState?: (party: ReviewParty) => { canToggle: boolean; isShowingCurrentValue: boolean };
    onToggle?: (party: ReviewParty) => void;
    errorText?: string;
}

/**
 * The party list as something the review can edit without saving. CounterPartiesStep cannot be reused
 * here: it POSTs, PUTs and DELETEs straight into the query cache on every edit, which is exactly what a
 * review waiting for Save must not do.
 */
function CounterPartiesReviewField({
    parties,
    selectableLegalEntities,
    isLoading,
    readOnly,
    onRemove,
    onToggleIncluded,
    onAdd,
    onShowInDocument,
    getToggleState,
    onToggle,
    errorText,
}: CounterPartiesReviewFieldProps) {
    const { translateString } = useTranslation();
    const styles = useContractAiUpdateStyles();

    return (
        <Box>
            <Typography sx={styles.partiesLabel}>{translateString("contractParties")}</Typography>

            <Box sx={styles.partyList}>
                {parties.map((party) => {
                    const toggleState = getToggleState?.(party);

                    return (
                        <Box key={party.key} sx={styles.partyRow}>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                {/* Name only, as the current column shows it: a matched party saves as its legal
                                    entity, so the address the AI read for it would never be saved anyway. */}
                                <Box sx={styles.partyNameLine}>
                                    {party.proposedNew && !readOnly ? (
                                        <FormControlLabel
                                            sx={{ ...styles.partyInlineControl, ...styles.partyCheckboxLabel }}
                                            control={<Checkbox size="small" checked={party.included} onChange={() => onToggleIncluded(party.key)} />}
                                            label={<OverflowTooltip text={party.name} sx={styles.partyName} />}
                                        />
                                    ) : (
                                        <OverflowTooltip text={party.name} sx={styles.partyName} />
                                    )}

                                    <Box sx={styles.partyCitation}>
                                        <ContractAiUpdateCitation citation={party.citation} onShowInDocument={onShowInDocument} />
                                    </Box>
                                </Box>

                                {party.proposedNew && <Typography sx={styles.partyDetail}>{translateString("partyWillBeCreated")}</Typography>}
                            </Box>

                            {!readOnly && (
                                <IconButton
                                    size="small"
                                    sx={styles.partyInlineControl}
                                    onClick={() => onRemove(party.key)}
                                    aria-label={translateString("delete")}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            )}

                            {/* The field rows toggle a whole value; a party list has to be toggled a party at a time,
                                so each party carries the control its row's arrow column would have held. */}
                            {onToggle && toggleState?.canToggle && (
                                <Box sx={styles.partyToggle}>
                                    <DotLegalTooltip text={translateString(toggleState.isShowingCurrentValue ? "useAiSuggestion" : "useCurrentValue")}>
                                        <IconButton size="small" onClick={() => onToggle(party)}>
                                            {toggleState.isShowingCurrentValue ? <ReplayIcon fontSize="small" /> : <ArrowForwardIcon fontSize="small" />}
                                        </IconButton>
                                    </DotLegalTooltip>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {errorText && (
                <Box data-validation-error sx={styles.partyListError}>
                    {errorText}
                </Box>
            )}

            {!readOnly && (
                <Box sx={{ marginTop: 1 }}>
                    <DotLegalSelectWithButton
                        label={translateString("addContractParty")}
                        placeholder={translateString("addContractParty")}
                        options={selectableLegalEntities?.filter((entity) => !parties.some((party) => party.legalEntityId === entity.id))}
                        selectedItem={null}
                        onChange={(legalEntityId) => {
                            if (legalEntityId) {
                                onAdd(legalEntityId);
                            }
                        }}
                        clearOnSelect
                        noOptionsLabel={translateString("noOptions")}
                        buttonLabel={translateString("createLegalEntity")}
                        onButtonClick={() => {}}
                        hasPermission={false}
                        isLoading={isLoading}
                    />
                </Box>
            )}
        </Box>
    );
}

export default CounterPartiesReviewField;
